import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, Home, Clock, Building2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavigationProps {
  onBookingClick: () => void;
  className?: string;
}

const Navigation = ({ onBookingClick, className = "" }: NavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const organizingServices = [
    {
      title: "Residential Organizing",
      description: "Whole-home, closets, kitchens, ADHD systems",
      href: "/organizing#residential",
      icon: Home
    },
    {
      title: "Commercial Organizing",
      description: "Office spaces & retail organization",
      href: "/organizing#commercial",
      icon: Building2
    },
    {
      title: "Same-Day Organizing",
      description: "Urgent organizing services",
      href: "/same-day-organizing",
      icon: Clock
    }
  ];

  const cleaningServices = [
    {
      title: "Residential Cleaning",
      description: "Basic, deep, move-in/move-out cleaning",
      href: "/cleaning#residential",
      icon: Home
    },
    {
      title: "Commercial Cleaning",
      description: "Office & retail space cleaning",
      href: "/cleaning#commercial",
      icon: Building2
    },
    {
      title: "Same-Day Cleaning",
      description: "Emergency cleaning services",
      href: "/same-day-cleaning", 
      icon: Clock
    }
  ];

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className={`flex items-center space-x-6 ${className}`}>
      {/* Organizing Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="font-medium">
            Organizing <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80">
          {organizingServices.map((service) => {
            const IconComponent = service.icon;
            return (
              <DropdownMenuItem
                key={service.href}
                onClick={() => navigate(service.href)}
                className="p-4 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <IconComponent className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <div className="font-medium">{service.title}</div>
                    <div className="text-sm text-muted-foreground">{service.description}</div>
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Cleaning Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="font-medium">
            Cleaning <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80">
          {cleaningServices.map((service) => {
            const IconComponent = service.icon;
            return (
              <DropdownMenuItem
                key={service.href}
                onClick={() => navigate(service.href)}
                className="p-4 cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <IconComponent className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <div className="font-medium">{service.title}</div>
                    <div className="text-sm text-muted-foreground">{service.description}</div>
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Direct Links */}
      <button
        onClick={() => scrollToSection("about")}
        className="text-foreground hover:text-primary transition-colors font-medium"
      >
        About
      </button>
      <button
        onClick={() => scrollToSection("contact")}
        className="text-foreground hover:text-primary transition-colors font-medium"
      >
        Contact
      </button>
      <button
        onClick={() => navigate("/store")}
        className="text-foreground hover:text-primary transition-colors font-medium"
      >
        Store
      </button>

      {/* CTA Button */}
      <Button onClick={onBookingClick} size="lg" className="luxury-gradient text-primary-foreground font-semibold">
        Book Now
      </Button>
    </nav>
  );
};

export default Navigation;